// ============================================================================
// PROJECT FILTER CONTEXT - Filter state and setters only
// ============================================================================
// What it does: Holds only filter state and setters. Search (keyword, name, description,
// tag), statuses, dates, active tab, project-specific "my role" filter and "include my
// public projects". It does NOT filter a list—it just stores what the user selected.
// ProjectDataContext and the filter sidebar read/write this state. Built on
// EntityFilterContext (which adds tab and base search/status/date).

import React, { createContext, useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { EntityFilterProvider, useEntityFilter } from '../../../shared/context/EntityFilterContext';
import { resetFiltersKeepingTab as resetFiltersKeepingTabUtil } from '../../../shared/searchPrefs/searchPrefsDefaultFilters';
import { SEARCH_RECORD_TYPE } from '../../../shared/searchPrefs/searchPrefsConstants';

// ============================================================================
// CONTEXT CREATION AND EXPORTS
// ============================================================================

const ProjectFilterContext = createContext();

export const useProjectFilter = () => useContext(ProjectFilterContext);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

// Internal component that uses EntityFilterContext
const ProjectFilterProviderInner = ({ children }) => {
  // Get entity filter functionality (includes tab management) from EntityFilterContext
  const entityFilter = useEntityFilter();
  
  // ============================================================================
  // PROJECT-SPECIFIC STATE (not in EntityFilterContext)
  // ============================================================================
  
  // My Role filter: "No role filter applied" (default) vs user selects mode and roles.
  const [noRoleFilterApplied, setNoRoleFilterAppliedState] = useState(true);
  const [roleMode, setRoleMode] = useState(null); // null | 'inherited' | 'exact'; null when noRoleFilterApplied
  const [inheritedTop, setInheritedTop] = useState(null); // 'OWNER' | 'CONTRIBUTOR' | 'VIEWER' | null
  const [exactSelected, setExactSelected] = useState([]); // string[]
  const [appliedRoleFilter, setAppliedRoleFilter] = useState(null); // null | string[] (userRoleSet sent to API)

  // Include my public projects: when true (default), list includes user's public projects.
  // When false, only non-public projects are shown.
  const [includeMyPublicProjects, setIncludeMyPublicProjects] = useState(true);
  const [noRatingFilterApplied, setNoRatingFilterAppliedState] = useState(true);
  const [selectedMinRating, setSelectedMinRating] = useState(null);
  
  // Flag to track if any filters are currently active (includes projectScope)
  // Used to show/hide filter-related UI elements
  const [isFiltering, setIsFiltering] = useState(false);

  // ============================================================================
  // PROJECT-SPECIFIC FILTER FUNCTIONS (My Role)
  // ============================================================================
  
  const ROLE_LEVEL = { OWNER: 2, CONTRIBUTOR: 1, VIEWER: 0 };

  const computeEffectiveRoleSet = useCallback((mode, top, exact) => {
    if (mode === 'exact') return exact.length > 0 ? [...exact] : [];
    if (mode !== 'inherited' || !top) return [];
    return ['OWNER', 'CONTRIBUTOR', 'VIEWER'].filter((r) => ROLE_LEVEL[r] <= ROLE_LEVEL[top]);
  }, []);

  const clearProjectScopeFilters = useCallback(() => {
    setAppliedRoleFilter(null);
    setInheritedTop(null);
    setExactSelected([]);
    setRoleMode(null);
    setNoRoleFilterAppliedState(true);
  }, []);

  const setNoRoleFilterApplied = useCallback((value) => {
    setNoRoleFilterAppliedState(value);
    if (value) {
      setAppliedRoleFilter(null);
      setInheritedTop(null);
      setExactSelected([]);
      setRoleMode(null);
    } else {
      // User unchecked "No role filter applied" → select Inherited roles by default with all roles
      setRoleMode('inherited');
      setInheritedTop('OWNER');
      setExactSelected([]);
    }
  }, []);

  const applyRoleFilter = useCallback(() => {
    const effective = computeEffectiveRoleSet(roleMode, inheritedTop, exactSelected);
    if (effective.length > 0) {
      setAppliedRoleFilter(effective);
    } else {
      setAppliedRoleFilter(null);
    }
    return effective;
  }, [roleMode, inheritedTop, exactSelected, computeEffectiveRoleSet]);

  const setRoleModeValue = useCallback((mode) => {
    if (mode !== 'inherited' && mode !== 'exact') return;
    setNoRoleFilterAppliedState(false); // Selecting a mode means user is using the filter → uncheck "No role filter applied"
    setRoleMode(mode);
    if (mode === 'exact') {
      setExactSelected(['OWNER', 'CONTRIBUTOR', 'VIEWER']);
      setInheritedTop(null);
    } else {
      setInheritedTop('OWNER');
      setExactSelected([]);
    }
  }, []);

  const setInheritedTopValue = useCallback((top) => {
    setInheritedTop(top);
  }, []);

  const toggleExactRole = useCallback((role) => {
    setExactSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  }, []);

  /** Apply an exact role list (used by filter presets; does not change role filter UI behavior). */
  const applyRolePresetFromList = useCallback((roles) => {
    if (!roles || roles.length === 0) {
      clearProjectScopeFilters();
      return;
    }
    setNoRoleFilterAppliedState(false);
    setRoleMode('exact');
    setExactSelected([...roles]);
    setInheritedTop(null);
    setAppliedRoleFilter([...roles]);
  }, [clearProjectScopeFilters]);

  // Toggle "Include my public projects" (sent as includeMyPublicProjects to backend)
  const setIncludeMyPublicProjectsValue = useCallback((value) => {
    setIncludeMyPublicProjects(value);
  }, []);

  const setMinRatingFilter = useCallback((ratingValue) => {
    setNoRatingFilterAppliedState(false);
    setSelectedMinRating(ratingValue);
  }, []);

  const clearRatingFilter = useCallback(() => {
    setSelectedMinRating(null);
  }, []);

  const setNoRatingFilterApplied = useCallback((value) => {
    setNoRatingFilterAppliedState(value);
    if (value) {
      setSelectedMinRating(null);
    } else {
      setSelectedMinRating(null);
    }
  }, []);

  // Enhanced clearAllFilters that also clears project scope and resets include my public projects
  const clearAllFilters = useCallback(() => {
    entityFilter.clearAllFilters();
    clearProjectScopeFilters();
    setIncludeMyPublicProjects(true);
    setNoRatingFilterAppliedState(true);
    setSelectedMinRating(null);
  }, [entityFilter, clearProjectScopeFilters]);

  const buildFilterResetActions = useCallback(
    () => ({
      setSelectedStatuses: entityFilter.setSelectedStatuses,
      setSearchByKeyword: entityFilter.setSearchByKeyword,
      setSearchByTitle: entityFilter.setSearchByTitle,
      setSearchByDescription: entityFilter.setSearchByDescription,
      setSearchByTag: entityFilter.setSearchByTag,
      setDateFilterMode: entityFilter.setDateFilterMode,
      setQueryFromDay: entityFilter.setQueryFromDay,
      setQueryToDay: entityFilter.setQueryToDay,
      setIsDateFilterActive: entityFilter.setIsDateFilterActive,
      setIncludeMyPublic: setIncludeMyPublicProjectsValue,
      clearRatingFilter,
      setNoRatingFilterApplied,
      clearRoleFilters: clearProjectScopeFilters,
      applyRolePreset: applyRolePresetFromList,
    }),
    [
      entityFilter,
      setIncludeMyPublicProjectsValue,
      clearRatingFilter,
      setNoRatingFilterApplied,
      clearProjectScopeFilters,
      applyRolePresetFromList,
    ]
  );

  const resetFiltersKeepingTab = useCallback(() => {
    resetFiltersKeepingTabUtil(buildFilterResetActions(), SEARCH_RECORD_TYPE.PROJECT);
  }, [buildFilterResetActions]);

  // ============================================================================
  // FILTER STATE DETECTION
  // ============================================================================
  
  // Effect to update isFiltering state whenever filter conditions change
  useEffect(() => {
    const roleFilterActive = appliedRoleFilter != null && appliedRoleFilter.length > 0;
    const isCurrentlyFiltering = 
      entityFilter.searchByKeyword !== "" ||
      entityFilter.searchByTitle !== "" || 
      entityFilter.searchByDescription !== "" || 
      entityFilter.searchByTag !== "" ||
      entityFilter.selectedStatuses.length > 0 ||
      roleFilterActive ||
      includeMyPublicProjects === false ||
      (!noRatingFilterApplied && selectedMinRating !== null);
    
    setIsFiltering(isCurrentlyFiltering);
  }, [entityFilter.searchByKeyword, entityFilter.searchByTitle, entityFilter.searchByDescription, entityFilter.searchByTag, entityFilter.selectedStatuses, appliedRoleFilter, includeMyPublicProjects, noRatingFilterApplied, selectedMinRating, isFiltering]);

  // ============================================================================
  // CONTEXT VALUE CREATION
  // ============================================================================
  
  // Create the context value that merges entity filter with project-specific additions
  // This maintains the exact same API as before, just implemented using EntityFilterContext
  const contextValue = useMemo(() => ({
    // ============================================================================
    // FILTER STATE (from EntityFilterContext)
    // ============================================================================
    selectedStatuses: entityFilter.selectedStatuses,
    selectedVisibilities: entityFilter.selectedVisibilities,
    searchByKeyword: entityFilter.searchByKeyword,
    searchByTitle: entityFilter.searchByTitle,
    searchByDescription: entityFilter.searchByDescription,
    searchByTag: entityFilter.searchByTag,
    dateFilterMode: entityFilter.dateFilterMode,
    queryFromDay: entityFilter.queryFromDay,
    queryToDay: entityFilter.queryToDay,
    isDateFilterActive: entityFilter.isDateFilterActive,
    
    // ============================================================================
    // PROJECT-SPECIFIC STATE
    // ============================================================================
    noRoleFilterApplied,
    roleMode,
    inheritedTop,
    exactSelected,
    appliedRoleFilter,               // null | string[] (sent as userRoleSet when non-empty)
    includeMyPublicProjects,
    noRatingFilterApplied,
    selectedMinRating,
    setIncludeMyPublicProjects: setIncludeMyPublicProjectsValue,
    isFiltering,
    
    // ============================================================================
    // TAB STATE (from EntityFilterContext)
    // ============================================================================
    activeTab: entityFilter.activeTab, // Currently active tab
    
    // ============================================================================
    // FILTER MANAGEMENT FUNCTIONS (from EntityFilterContext)
    // ============================================================================
    clearAllFilters,                 // Enhanced to also clear project scope
    resetFiltersKeepingTab,
    clearSearchFilters: entityFilter.clearSearchFilters,
    clearStatusFilters: entityFilter.clearStatusFilters,
    clearVisibilityFilters: entityFilter.clearVisibilityFilters,
    clearDateFilters: entityFilter.clearDateFilters,
    
    // ============================================================================
    // PROJECT-SPECIFIC FILTER FUNCTIONS
    // ============================================================================
    clearProjectScopeFilters,        // Function to clear project scope filters only
    
    // ============================================================================
    // SEARCH FUNCTIONS (from EntityFilterContext)
    // ============================================================================
    setSearchByKeyword: entityFilter.setSearchByKeyword,
    setSearchByTitle: entityFilter.setSearchByTitle,
    setSearchByDescription: entityFilter.setSearchByDescription,
    setSearchByTag: entityFilter.setSearchByTag,
    
    // ============================================================================
    // STATUS FILTER FUNCTIONS (from EntityFilterContext)
    // ============================================================================
    setSelectedStatuses: entityFilter.setSelectedStatuses,
    toggleStatusSelection: entityFilter.toggleStatusSelection,
    setMinRatingFilter,
    clearRatingFilter,
    setNoRatingFilterApplied,
    
    // ============================================================================
    // VISIBILITY FILTER FUNCTIONS (from EntityFilterContext)
    // ============================================================================
    setSelectedVisibilities: entityFilter.setSelectedVisibilities,
    toggleVisibilitySelection: entityFilter.toggleVisibilitySelection,
    
    // ============================================================================
    // MY ROLE FILTER FUNCTIONS
    // ============================================================================
    setNoRoleFilterApplied,
    setRoleMode: setRoleModeValue,
    setInheritedTop: setInheritedTopValue,
    toggleExactRole: toggleExactRole,
    applyRoleFilter,
    applyRolePresetFromList,
    
    // ============================================================================
    // DATE FILTER FUNCTIONS (from EntityFilterContext)
    // ============================================================================
    setDateFilterMode: entityFilter.setDateFilterMode,
    setQueryFromDay: entityFilter.setQueryFromDay,
    setQueryToDay: entityFilter.setQueryToDay,
    setIsDateFilterActive: entityFilter.setIsDateFilterActive,
    
    // ============================================================================
    // TAB MANAGEMENT FUNCTIONS (from EntityFilterContext)
    // ============================================================================
    setActiveTab: entityFilter.setActiveTab,
  }), [
    entityFilter,
    noRoleFilterApplied,
    roleMode,
    inheritedTop,
    exactSelected,
    appliedRoleFilter,
    includeMyPublicProjects,
    noRatingFilterApplied,
    selectedMinRating,
    isFiltering,
    clearAllFilters,
    resetFiltersKeepingTab,
    clearProjectScopeFilters,
    setNoRoleFilterApplied,
    setRoleModeValue,
    setInheritedTopValue,
    toggleExactRole,
    applyRoleFilter,
    applyRolePresetFromList,
    setIncludeMyPublicProjectsValue,
    setMinRatingFilter,
    clearRatingFilter,
    setNoRatingFilterApplied,
  ]);

  return (
    <ProjectFilterContext.Provider value={contextValue}>
      {children}
    </ProjectFilterContext.Provider>
  );
};

// Outer provider that wraps EntityFilterProvider
export const ProjectFilterProvider = ({ children }) => {
  return (
    <EntityFilterProvider entityType="project" defaultTab="myProjects">
      <ProjectFilterProviderInner>
        {children}
      </ProjectFilterProviderInner>
    </EntityFilterProvider>
  );
};
